<template>
  <b-modal :active.sync="opened" width="350px" scroll="keep">
    <div class="card">
      <div class="card-content tw-p-6">
        <div class="section-block tw-mb-5">
          <div class="title">Edit Categories</div>
        </div>
        <div class="columns">
          <div class="column">
            <form action="">
              <!-- Select the category to edit -->
              <b-field grouped class="tw-items-end columns" v-if="!isEditing">
                <validation-provider
                  name="event_category_id"
                  rules="required"
                  v-slot="{ errors }"
                  class="column cat"
                >
                  <b-field
                    label="Category"
                    :message="errors"
                    :type="{ 'is-danger': errors.length }"
                    expanded
                  >
                    <b-select
                      expanded
                      v-model="editCategory.selected"
                      :placeholder="editCategory.label"
                      :disabled="isLoading"
                      :loading="isLoading"
                      @input="setCategorySelected()"
                    >
                      <option
                        v-for="category in calendarCategories"
                        :value="category"
                        :key="`cat-` + category.id"
                        >{{ category.name }}</option
                      >
                    </b-select>
                  </b-field>
                </validation-provider>

                <b-field class="column tw-text-right is-one-quarter">
                  <v-swatches
                    :value="category.color"
                    shapes="circles"
                    :trigger-style="swatch.style"
                    class="tw-mx-auto"
                    disabled
                  ></v-swatches>
                </b-field>
              </b-field>

              <p class="tw--mt-10 tw-z-5" v-if="!isEditing && category.id">
                <small
                  ><a
                    class="tw-cursor-pointer"
                    @click.prevent="isEditing = true"
                    >Edit</a
                  ></small
                >
              </p>

              <!-- Editing the Category -->
              <validation-observer v-slot="{ valid }" ref="editCat">
                <b-field
                  grouped
                  class="cat tw-items-end columns"
                  v-if="isEditing"
                  expanded
                >
                  <validation-provider
                    name="name"
                    rules="required"
                    v-slot="{ errors }"
                    class="column "
                  >
                    <b-field
                      label="Category Name"
                      :message="errors"
                      :type="{ 'is-danger': errors.length }"
                    >
                      <b-input v-model="category.name"></b-input>
                    </b-field>
                  </validation-provider>
                  <validation-provider
                    name="color"
                    rules="required"
                    v-slot="{ errors }"
                    class="column is-one-quarter"
                  >
                    <b-field
                      label="Set Color"
                      class="tw-text-right cat-swatch"
                      :message="errors"
                      :type="{ 'is-danger': errors.length }"
                      expanded
                    >
                      <v-swatches
                        class="tw-mx-auto"
                        v-model="category.color"
                        swatch-size="24"
                        shapes="circles"
                        popover-x="left"
                        popover-y="bottom"
                        :trigger-style="swatch.style"
                      ></v-swatches>
                    </b-field>
                  </validation-provider>
                </b-field>

                <div
                  class="tw--mt-6 level tw-flex tw-items-end"
                  v-if="isEditing"
                >
                  <div class="level-left">
                    <small
                      ><a
                        class="tw-cursor-pointer"
                        @click.prevent="isEditing = false"
                        >Cancel</a
                      ></small
                    >
                    <b-button
                      class="tw-ml-5 tw-px-2 tw-py-1 tw-min-w-0"
                      size="is-small"
                      type="is-primary"
                      :loading="isLoading"
                      :disabled="isLoading || !valid"
                      @click.prevent="updateCategory()"
                      >Save</b-button
                    >
                  </div>
                  <div class="level-right">
                    <small>
                      <a
                        class="has-text-danger"
                        @click.prevent="deleteCategory()"
                        >Delete Category</a
                      >
                    </small>
                  </div>
                </div>
                <a
                  class="tw-mt-5 tw-inline-block"
                  v-if="calendarAction"
                  @click.prevent="goToForm()"
                  >&#x2190; Back</a
                >
              </validation-observer>
            </form>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import VSwatches from 'vue-swatches'

export default {
  props: {
    event: { type: Object }
  },
  data() {
    return {
      opened: false,
      isLoading: false,
      isEditing: false,
      category: {
        id: '',
        name: '',
        color: ''
      },
      editCategory: {
        selected: null,
        label: 'Select -'
      },
      swatch: {
        style: {
          width: '32px',
          height: '32px'
        }
      },
      prevAction: ''
    }
  },
  components: { VSwatches },
  computed: {
    ...mapState({
      calendarCategories: state => state.group.calendar.categories,
      calendarAction: state => state.calendar.action
    })
  },
  methods: {
    ...mapActions({
      updateCalendarCategories: 'group/updateCalendarCategories',
      getCalendarCategories: 'group/getCalendarCategories',
      deleteCalendarCategories: 'group/deleteCalendarCategories'
    }),
    ...mapMutations(['setCalendarAction']),

    open() {
      this.prevAction = this.calendarAction
      this.opened = true
      this.setCalendarAction('edit-categories')
    },
    clearCategory() {
      this.category = {
        id: '',
        name: '',
        color: ''
      }
    },
    setCategorySelected() {
      this.category = this.editCategory.selected
    },
    updateCategory() {
      this.isLoading = true
      this.updateCalendarCategories(this.category)
        .then(() => {
          this.getCalendarCategories()
          this.isEditing = false
        })
        .catch(error => {
          this.$refs.editCat.setErrors(error)
        })
        .finally(async () => {
          this.isLoading = false
          this.editCategory.selected = null
          await this.$refs.editCat.reset()
          this.clearCategory()
        })
    },
    deleteCategory() {
      this.isLoading = true
      this.deleteCalendarCategories(this.category)
        .then(() => {
          this.getCalendarCategories()
          this.isEditing = false
        })
        .catch(error => {
          this.$refs.editCat.setErrors(error)
        })
        .finally(async () => {
          this.isLoading = false
          this.editCategory.selected = null
          await this.$refs.editCat.reset()
          this.clearCategory()
        })
    },
    goToForm() {
      this.opened = false
      this.$emit('closed')
      this.setCalendarAction(this.prevAction)
    }
  }
}
</script>

<style lang="sass">
.card-content
  overflow: auto

.button.is-small
  min-width: auto
</style>
